import { useEffect } from "react";

import { useNavigate } from "react-router-dom";

import { useAtom } from "jotai";

import { accessKeyAtom, userIdAtom, userAtom } from "@/atoms/auth.atoms";

import authService from "@/services/auth.service";

import { HttpError } from "@/services/api";

const IS_ELECTRON =
  typeof window !== "undefined" && Boolean(window.electronAPI);

/**
 * Bootstraps the user on mount and listens for Electron deep-link auth.
 *
 * In browser PWA mode there's no accessKey to inspect (the HttpOnly session
 * cookie is set by the backend on login and rides on every request via
 * credentials:include); we just call /auth/me and trust the result.
 *
 * In Electron we additionally listen for protocol auth events (cold-start
 * pull + runtime push). The accessKey arrives via IPC from the desktop
 * app's deep-link handler, gets persisted to localStorage so api.ts can
 * attach the Authorization header on subsequent calls.
 */
export function useProtocolAuth() {
  const navigate = useNavigate();

  const [accessKey, setAccessKey] = useAtom(accessKeyAtom);

  const [, setUserId] = useAtom(userIdAtom);

  const [user, setUser] = useAtom(userAtom);

  // Restore session: ALWAYS try /me on mount. In browser the cookie
  // authenticates the call; in Electron the Authorization header (from
  // localStorage) does.
  useEffect(() => {
    if (user) return;
    // In Electron, only attempt if we actually have a token — saves a
    // pointless 401 on a fresh install.
    if (IS_ELECTRON && !accessKey) return;

    authService
      .getMe()
      .then((u) => setUser(u))
      .catch((err) => {
        if (err instanceof HttpError && err.status === 401) {
          if (IS_ELECTRON) {
            localStorage.removeItem("@Guest/accessKey");
            localStorage.removeItem("@Guest/userId");
            setAccessKey(null);
            setUserId(null);
          }
          // Browser: nothing to clear — there's no JS-readable credential.
        }
      });
  }, [accessKey, user, setUser, setAccessKey, setUserId]);

  // Protocol auth from Electron deep links
  useEffect(() => {
    const electronAPI = window.electronAPI;
    if (!electronAPI) return;

    const applyAuth = async (data: { accessKey: string; userId: string }) => {
      const { accessKey: deepLinkKey, userId: deepLinkUserId } = data;

      if (!deepLinkKey || !deepLinkUserId) return;

      localStorage.setItem("@Guest/accessKey", deepLinkKey);

      localStorage.setItem("@Guest/userId", deepLinkUserId);

      setAccessKey(deepLinkKey);

      setUserId(deepLinkUserId);

      // Trade the Electron-local accessKey for HttpOnly session cookies
      // in the WebView. Once this lands, subsequent calls authenticate via
      // cookie even in Electron — closer to the browser PWA model. If it
      // fails (older backend without the endpoint, transient network), we
      // still have the Authorization header fallback in api.ts, so the
      // user is not stuck. Eventual Phase 5 cleanup can drop the header.
      try {
        await authService.upgradeSession();
      } catch {
        /* keep Authorization header path */
      }

      try {
        const me = await authService.getMe();
        setUser(me);
      } catch {
        /* ignore */
      }

      navigate("/connection-mode");
    };

    // Pull: check for pending auth from cold start
    electronAPI.getPendingAuth?.().then((data) => {
      if (data) applyAuth(data);
    });

    // Push: listen for auth when app is already running
    electronAPI.onProtocolAuth?.((data) => {
      applyAuth(data);
    });
  }, [setAccessKey, setUserId, setUser, navigate]);
}
